import { computed, onScopeDispose, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import {
  getTornadoChartFrequencies,
  serializeScenario
} from "./tornadoChartJobs";
import { calculationInputsJson, hashKey } from "./tornadoCacheKey";
import { acquirePrediction, type PredictionLease } from "./tornadoPredictions";

// Predicts the tornado chart's flow rates in the background before the chart
// is opened, so it is often ready, or nearly, when someone switches to it.
// Runs once edits have settled, two frequencies at a time so it stays light;
// a chart that opens meanwhile shares the same jobs (see tornadoPredictions).

const SETTLE_MS = 2000;
const CONCURRENCY = 2;

export const usePrefetchTornadoPredictions = (options: {
  scenarioCalculations: ScenarioCalculations;
  minFrequency: () => number;
  maxFrequency: () => number;
}) => {
  const scenario = computed(() =>
    serializeScenario(options.scenarioCalculations.destructuredScenario)
  );
  const inputsJson = computed(() => calculationInputsJson(scenario.value));

  let generation = 0;
  let leases: PredictionLease[] = [];
  const stop = () => {
    generation++;
    leases.forEach(lease => lease.release());
    leases = [];
  };

  const prefetch = async () => {
    stop();
    const run = generation;
    const isCurrent = () => run === generation;
    const current = scenario.value;
    const scenarioHash = await hashKey(inputsJson.value);
    const queue = getTornadoChartFrequencies(
      current,
      options.minFrequency(),
      options.maxFrequency()
    );
    const lane = async () => {
      while (isCurrent() && queue.length > 0) {
        const lease = acquirePrediction(scenarioHash, current, queue.shift()!);
        leases.push(lease);
        await lease.promise.catch(() => undefined);
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, lane));
    // Everything is cached now; nothing left to hold.
    if (isCurrent()) stop();
  };
  const prefetchSoon = useDebounceFn(prefetch, SETTLE_MS);

  watch(
    inputsJson,
    () => {
      // Drop work for inputs that no longer apply straight away.
      stop();
      prefetchSoon();
    },
    { immediate: true }
  );

  onScopeDispose(stop);
};
